import { contentListSearchFrom, validateContentListSearch } from "@dashboard/lib/content/content-list-search"
import { contentListQueryOptions } from "@dashboard/lib/content/content-queries"
import { listContentDocuments } from "@dashboard/lib/content/content-server"
import { dashboardRolesQueryOptions, dashboardUsersQueryOptions } from "@dashboard/lib/users/users-queries"
import {
  dashboardContentTypeFromPathSegment,
  dashboardPathForSection,
  dashboardSectionFromPathSegment,
  defaultDashboardContentType,
} from "@dashboard/routes/dashboard-navigation"
import { LazyDashboardApp } from "@dashboard/routes/lazy-dashboard-app"
import { dashboardAppHostRedirectUrl } from "@dashboard-config/dashboard-host"
import { currentRequestHostname } from "@dashboard-config/dashboard-host-runtime"
import { createFileRoute, redirect } from "@tanstack/react-router"
import {
  DashboardSectionSkeleton,
  dashboardSectionSkeletonChrome,
} from "@ui-dashboard/DashboardSectionSkeleton"
import { DashboardShellSkeleton } from "@ui-dashboard/DashboardShellSkeleton"
import { Suspense } from "react"

export const Route = createFileRoute("/dashboard/$section")({
  component: DashboardSectionRoute,
  /**
   * The list's filters, as URL state.
   *
   * They used to be Jotai atoms, which meant the server had no way of knowing
   * which list a request wanted — so the table could not start loading until
   * the browser had mounted the app and the atoms had said. Declaring them here
   * is what lets the loader below resolve the rows alongside the document.
   */
  validateSearch: validateContentListSearch,
  beforeLoad: async ({ location, params }) => {
    const redirectHref = dashboardAppHostRedirectUrl(await currentRequestHostname(), location.href)
    if (redirectHref) throw redirect({ href: redirectHref })

    const section = dashboardSectionFromPathSegment(params.section)
    if (!section) {
      throw redirect({ href: dashboardPathForSection(defaultDashboardContentType) })
    }
  },
  loaderDeps: ({ search }) => contentListSearchFrom(search),
  /**
   * Two things start here, and the order matters to neither.
   *
   * The chunk imports were already here: starting them while the route
   * resolves moves them alongside the document instead of after it, so the
   * fallback below usually never renders.
   *
   * The list data is new. Fetching it in the loader is what the public routes
   * have always done — see `$locale/news/index.tsx` — and it removes a whole
   * round trip from first paint: the rows now travel with the HTML instead of
   * waiting for hydration to ask for them.
   */
  loader: async ({ context, deps, params }) => {
    const chunks =
      typeof window === "undefined"
        ? null
        : (void import("@dashboard/routes/DashboardApp"),
          void import("@dashboard/components/navigation/dashboard-sections"),
          null)

    /*
     * The admin sections start their fetch here too.
     *
     * They used to fall out of this loader before it reached the prefetch, so
     * Users and Roles only began asking for data once the browser had mounted
     * the view — a whole round trip after the content sections, which start
     * theirs alongside the chunk. Nothing is awaited on the client, exactly as
     * below: the fetch is begun, not waited for.
     */
    const section = dashboardSectionFromPathSegment(params.section)
    if (section === "users" || section === "roles") {
      const admin =
        section === "users"
          ? context.queryClient.ensureQueryData(dashboardUsersQueryOptions())
          : context.queryClient.ensureQueryData(dashboardRolesQueryOptions())
      const settled = admin.catch(() => undefined)
      if (typeof window === "undefined") await settled
      return chunks
    }

    const contentType = dashboardContentTypeFromPathSegment(params.section)
    if (!contentType) return chunks

    /**
     * A failure here must not take the section down with it. Someone without
     * read permission on this type, or a backend having a bad moment, should
     * still get the app — which will ask again on mount and render the error
     * in the table where it belongs, exactly as it did before this prefetch
     * existed.
     */
    const rows = context.queryClient
      .ensureQueryData(
        contentListQueryOptions({
          fetchDocuments: (input) => listContentDocuments({ data: input }),
          language: deps.language,
          page: deps.page,
          pageSize: deps.pageSize,
          query: deps.query,
          status: deps.status,
          team: deps.team,
          type: contentType,
        })
      )
      .catch(() => undefined)

    /**
     * Awaited for the document, not for a tab change.
     *
     * A loader holds the navigation open until it resolves, and the router
     * keeps rendering the section you are leaving until it does. On the server
     * that is the whole point: waiting is what puts the rows in the HTML and
     * saves a round trip on a cold load. In the browser it is the opposite —
     * the app is already mounted, the table has a skeleton and a `useQuery`
     * that will fill it, and blocking here only means the old section sits on
     * screen with both tabs lit while the fetch runs.
     *
     * So the fetch starts either way, and only the server waits for it.
     */
    if (typeof window === "undefined") await rows

    return chunks
  },
})

function DashboardSectionRoute() {
  const { section } = Route.useParams()
  const dashboardSection = dashboardSectionFromPathSegment(section) ?? defaultDashboardContentType

  /**
   * The chrome as well as the page.
   *
   * This boundary suspends on `LazyDashboardApp`, which renders the sidebar,
   * the service tabs and the section — all of it. The fallback stood in for
   * the section alone, so while that chunk arrived there was a table-shaped
   * placeholder floating with no sidebar beside it and no tabs above it, and
   * then the chrome appeared around it. A placeholder has to be the shape of
   * what is missing.
   *
   * The section skeleton is passed straight through to the same slot the shell
   * gives the page, and the boundary inside the app uses that same component —
   * so when the chunk lands, the chrome fills in around content that does not
   * move, rather than one placeholder being swapped for another.
   */
  return (
    <Suspense
      fallback={
        <DashboardShellSkeleton>
          <DashboardSectionSkeleton {...dashboardSectionSkeletonChrome(dashboardSection)} />
        </DashboardShellSkeleton>
      }
    >
      <LazyDashboardApp activeSection={dashboardSection} />
    </Suspense>
  )
}
